// ========== THEMES DISPONIBLES ==========

export interface ThemeColors {
  primary: string;
  primaryDark: string;
  secondary: string;
  accent: string;
  accentDark: string;
  background: string;
  surface: string;
  border: string;
  text: string;
  textPrimary: string;
  textSecondary: string;
  success: string;
  error: string;
  warning: string;
  info: string;
  white: string;
}

export interface ThemeConfig {
  colors: ThemeColors;
  typography: {
    fontFamily: string;
    sizes: {
      xs: string;
      sm: string;
      md: string;
      base: string;
      lg: string;
      xl: string;
      '2xl': string;
      '3xl': string;
      '4xl': string;
      '5xl': string;
    };
    weights: {
      normal: number;
      medium: number;
      semibold: number;
      bold: number;
    };
  };
  layout: {
    topBarHeight: string;
    sidebarWidth: string;
  };
  spacing: {
    xs: string;
    sm: string;
    md: string;
    lg: string;
    xl: string;
    xxl: string;
    '2xl': string;
    '3xl': string;
    '4xl': string;
  };
  borderRadius: {
    sm: string;
    md: string;
    lg: string;
    xl: string;
    full: string;
  };
  shadows: {
    sm: string;
    md: string;
    lg: string;
    xl: string;
  };
  breakpoints: {
    xs: string;
    sm: string;
    md: string;
    lg: string;
    xl: string;
    '2xl': string;
  };
  transitions: {
    fast: string;
    normal: string;
    medium: string;
    base: string;
    slow: string;
  };
}

// ========== THÈME 1: BLEU PROFESSIONNEL & BRONZE ==========
export const sageTheme: ThemeConfig = {
  colors: {
    primary: '#26537E', // Bleu professionnel
    primaryDark: '#1D3F5F', // Bleu professionnel foncé
    secondary: '#AC7F00', // Bronze/Or
    accent: '#AC7F00', // Bronze/Or
    accentDark: '#8A6600', // Bronze foncé (hover)
    background: '#F0F3F2', // Blanc cassé
    surface: '#ECECEC', // Gris clair
    border: '#E0E0E0', // Bordure fine
    text: '#191919', // Noir profond
    textPrimary: '#191919', // Alias pour text
    textSecondary: '#6B6B6B', // Texte secondaire gris
    success: '#4CAF50',
    error: '#F44336',
    warning: '#FF9800',
    info: '#2196F3',
    white: '#FFFFFF',
  },
  typography: {
    fontFamily: "'Sometype Mono', monospace",
    sizes: {
      xs: '0.75rem',
      sm: '0.875rem',
      md: '0.9375rem',
      base: '1rem',
      lg: '1.125rem',
      xl: '1.25rem',
      '2xl': '1.5rem',
      '3xl': '1.75rem',
      '4xl': '2.25rem',
      '5xl': '3rem',
    },
    weights: {
      normal: 400,
      medium: 500,
      semibold: 600,
      bold: 700,
    },
  },
  layout: {
    topBarHeight: '64px',
    sidebarWidth: '240px',
  },
  spacing: {
    xs: '0.25rem',
    sm: '0.5rem',
    md: '1rem',
    lg: '1.5rem',
    xl: '2rem',
    xxl: '2.5rem',
    '2xl': '3rem',
    '3xl': '4rem',
    '4xl': '5rem',
  },
  borderRadius: {
    sm: '4px',
    md: '8px',
    lg: '12px',
    xl: '16px',
    full: '9999px',
  },
  shadows: {
    sm: '0 1px 2px 0 rgba(0, 0, 0, 0.04)',
    md: '0 2px 8px rgba(0, 0, 0, 0.04)',
    lg: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
    xl: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
  },
  breakpoints: {
    xs: '320px',
    sm: '640px',
    md: '768px',
    lg: '1024px',
    xl: '1280px',
    '2xl': '1536px',
  },
  transitions: {
    fast: '150ms ease',
    normal: '200ms ease',
    medium: '250ms ease',
    base: '200ms ease',
    slow: '300ms ease',
  },
};

// ========== THÈME 2: PROFESSIONAL DARK ==========
export const professionalDarkTheme: ThemeConfig = {
  colors: {
    primary: '#26537E', // Bleu professionnel - Menu, titres
    primaryDark: '#1D3F5F', // Bleu professionnel foncé (hover)
    secondary: '#AC7F00', // Bronze/Or - Actions, icônes
    accent: '#AC7F00', // Bronze/Or
    accentDark: '#8A6600', // Bronze foncé (hover)
    background: '#FFFFFF', // Blanc pur - Pages, cartes
    surface: '#E5E5E5', // Gris neutre - Tableaux, séparateurs
    border: '#D0D0D0', // Bordure grise
    text: '#000000', // Noir - Contenu principal
    textPrimary: '#14213D', // Bleu marine - Titres
    textSecondary: '#5A5A5A', // Gris foncé - Contenu secondaire
    success: '#4CAF50',
    error: '#F44336',
    warning: '#FCA311',
    info: '#2196F3',
    white: '#FFFFFF',
  },
  typography: {
    fontFamily: "'Sometype Mono', monospace",
    sizes: {
      xs: '0.75rem',
      sm: '0.875rem',
      md: '0.9375rem',
      base: '1rem',
      lg: '1.125rem',
      xl: '1.25rem',
      '2xl': '1.5rem',
      '3xl': '1.75rem',
      '4xl': '2.25rem',
      '5xl': '3rem',
    },
    weights: {
      normal: 400,
      medium: 500,
      semibold: 600,
      bold: 700,
    },
  },
  layout: {
    topBarHeight: '64px',
    sidebarWidth: '240px',
  },
  spacing: {
    xs: '0.25rem',
    sm: '0.5rem',
    md: '1rem',
    lg: '1.5rem',
    xl: '2rem',
    xxl: '2.5rem',
    '2xl': '3rem',
    '3xl': '4rem',
    '4xl': '5rem',
  },
  borderRadius: {
    sm: '4px',
    md: '8px',
    lg: '12px',
    xl: '16px',
    full: '9999px',
  },
  shadows: {
    sm: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
    md: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
    lg: '0 10px 15px -3px rgba(0, 0, 0, 0.15)',
    xl: '0 20px 25px -5px rgba(0, 0, 0, 0.15)',
  },
  breakpoints: {
    xs: '320px',
    sm: '640px',
    md: '768px',
    lg: '1024px',
    xl: '1280px',
    '2xl': '1536px',
  },
  transitions: {
    fast: '150ms ease',
    normal: '200ms ease',
    medium: '250ms ease',
    base: '200ms ease',
    slow: '300ms ease',
  },
};

// ========== TYPES DE THÈMES ==========
export type ThemeName = 'sage' | 'professional-dark';

export interface ThemeMetadata {
  id: ThemeName;
  name: string;
  description: string;
  preview: {
    primary: string;
    secondary: string;
    background: string;
  };
}

export const availableThemes: Record<ThemeName, ThemeMetadata> = {
  'sage': {
    id: 'sage',
    name: 'Bleu & Bronze',
    description: 'Thème professionnel avec bleu et bronze',
    preview: {
      primary: '#26537E',
      secondary: '#AC7F00',
      background: '#F0F3F2',
    },
  },
  'professional-dark': {
    id: 'professional-dark',
    name: 'Professional Dark',
    description: 'Thème professionnel avec bleu et bronze sur fond clair',
    preview: {
      primary: '#26537E',
      secondary: '#AC7F00',
      background: '#FFFFFF',
    },
  },
};

// ========== HELPER ==========
export function getThemeConfig(themeName: ThemeName): ThemeConfig {
  switch (themeName) {
    case 'professional-dark':
      return professionalDarkTheme;
    case 'sage':
    default:
      return sageTheme;
  }
}
